import React from 'react';

const Terms = () => {
  return (
    <div className="legal-page-container">
      <div className="legal-content">
        <h1 className="legal-title">Terms & Conditions</h1>
        <p className="legal-updated">Last Updated: December 2025</p>

        <section className="legal-section">
          <h2 className="legal-heading">Acceptance of Terms</h2>
          <p className="legal-text">
            By accessing or using Find Clarity mobile application ("App"), you agree to be bound by these Terms & Conditions ("Terms"). If you do not agree to these Terms, please do not use the App.
          </p>
          <p className="legal-text">
            These Terms constitute a legally binding agreement between you ("User") and Find Clarity ("we", "us", or "our"), operating under the laws of India.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Service Description</h2>
          <p className="legal-text">
            Find Clarity is an AI-powered clarity and decision-making tool that provides:
          </p>
          <ul className="legal-list">
            <li>Structured guidance on questions related to career, life, relationships, and personal growth</li>
            <li>AI-generated responses to help organize thoughts and provide actionable insights</li>
            <li>A platform for better thinking and decision-making</li>
          </ul>
          <p className="legal-text">
            <strong>Important Disclaimer:</strong> Find Clarity is NOT a substitute for professional medical, mental health, legal, or financial advice. For serious concerns, please consult qualified professionals.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Eligibility</h2>
          <p className="legal-text">
            You must be at least 18 years old to use Find Clarity. By using the App, you represent and warrant that you meet this age requirement and have the legal capacity to enter into these Terms.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">User Account and Responsibilities</h2>
          <p className="legal-text">When using Find Clarity, you agree to:</p>
          <ul className="legal-list">
            <li>Provide accurate and current information if creating an account</li>
            <li>Maintain the security of your account credentials</li>
            <li>Be responsible for all activities under your account</li>
            <li>Notify us immediately of any unauthorized use</li>
            <li>Use the App only for lawful purposes</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Prohibited Uses</h2>
          <p className="legal-text">You may NOT use Find Clarity to:</p>
          <ul className="legal-list">
            <li>Violate any applicable laws or regulations in India or your jurisdiction</li>
            <li>Harass, abuse, or harm others</li>
            <li>Submit false, misleading, or malicious information</li>
            <li>Attempt to hack, disrupt, or compromise the App's security</li>
            <li>Use automated systems (bots, scrapers) without permission</li>
            <li>Reverse engineer or copy any part of the App</li>
            <li>Use the service for any commercial purpose without authorization</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">AI-Generated Content Disclaimer</h2>
          <p className="legal-text">
            <strong>Important:</strong> All guidance provided by Find Clarity is generated by artificial intelligence. While we strive for accuracy and helpfulness:
          </p>
          <ul className="legal-list">
            <li>AI responses may not always be accurate, complete, or suitable for your specific situation</li>
            <li>You should use your own judgment and critical thinking when evaluating AI guidance</li>
            <li>Find Clarity is not liable for decisions made based on AI-generated content</li>
            <li>For professional advice (medical, legal, financial), consult qualified experts</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Intellectual Property Rights</h2>
          <p className="legal-text">
            All content, features, functionality, and design of Find Clarity are owned by us and protected by Indian and international intellectual property laws.
          </p>
          <p className="legal-text">
            You retain ownership of the questions and content you submit. By using the App, you grant us a license to use this content solely to provide and improve our services.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Service Availability</h2>
          <p className="legal-text">
            We strive to provide uninterrupted service, but we do not guarantee:
          </p>
          <ul className="legal-list">
            <li>That the App will always be available or error-free</li>
            <li>That defects will be corrected immediately</li>
            <li>That the App is free from viruses or harmful components</li>
          </ul>
          <p className="legal-text">
            We reserve the right to modify, suspend, or discontinue the App at any time without notice.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Free Service</h2>
          <p className="legal-text">
            Find Clarity is currently provided free of charge. We reserve the right to:
          </p>
          <ul className="legal-list">
            <li>Introduce paid features or premium tiers in the future</li>
            <li>Modify or limit free service offerings</li>
            <li>Provide advance notice of any changes to the free service model</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Limitation of Liability</h2>
          <p className="legal-text">
            To the maximum extent permitted by Indian law, Find Clarity and its operators shall not be liable for:
          </p>
          <ul className="legal-list">
            <li>Any indirect, incidental, special, or consequential damages</li>
            <li>Loss of profits, data, or business opportunities</li>
            <li>Decisions made based on AI-generated guidance</li>
            <li>Service interruptions or technical issues</li>
            <li>Unauthorized access to your data due to circumstances beyond our control</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Indemnification</h2>
          <p className="legal-text">
            You agree to indemnify and hold harmless Find Clarity from any claims, damages, losses, or expenses arising from:
          </p>
          <ul className="legal-list">
            <li>Your violation of these Terms</li>
            <li>Your use or misuse of the App</li>
            <li>Your violation of any third-party rights</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Governing Law and Dispute Resolution</h2>
          <p className="legal-text">
            These Terms are governed by the laws of India. Any disputes arising from these Terms or your use of Find Clarity shall be subject to the exclusive jurisdiction of the courts in Bangalore, Karnataka, India.
          </p>
          <p className="legal-text">
            We encourage users to contact us first to resolve any disputes informally before pursuing legal action.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Termination</h2>
          <p className="legal-text">
            We reserve the right to terminate or suspend your access to Find Clarity immediately, without notice, for:
          </p>
          <ul className="legal-list">
            <li>Violation of these Terms</li>
            <li>Fraudulent or illegal activity</li>
            <li>Behavior that harms other users or the service</li>
          </ul>
          <p className="legal-text">
            You may stop using the App at any time. Upon termination, your data will be handled according to our Privacy Policy.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Changes to Terms</h2>
          <p className="legal-text">
            We may update these Terms from time to time. We will notify users of material changes via:
          </p>
          <ul className="legal-list">
            <li>In-app notifications</li>
            <li>Email (if you've provided one)</li>
            <li>Updated "Last Updated" date on this page</li>
          </ul>
          <p className="legal-text">
            Your continued use of Find Clarity after changes constitutes acceptance of the updated Terms.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Severability</h2>
          <p className="legal-text">
            If any provision of these Terms is found to be invalid or unenforceable under Indian law, the remaining provisions will continue in full force and effect.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Contact Information</h2>
          <p className="legal-text">
            For questions, concerns, or feedback regarding these Terms, please contact us:
          </p>
          <ul className="legal-list">
            <li>Email: <a href="mailto:shivam@findclarity.app" className="legal-link">shivam@findclarity.app</a></li>
            <li>Response Time: We aim to respond within 7 business days</li>
          </ul>
        </section>

        <section className="legal-section">
          <p className="legal-disclaimer">
            <strong>Legal Notice:</strong> These Terms & Conditions are drafted to comply with Indian laws including the Information Technology Act, 2000, Consumer Protection Act, 2019, and related regulations. This is a general template and should be reviewed by a qualified legal professional before being considered legally binding.
          </p>
        </section>
      </div>
    </div>
  );
};

export default Terms;
